import { format } from 'date-fns';
import { OrderHistory } from '../../stores/appOrderHistoryStore';
import { getTableFormattedDate, toDate } from '../../utils/dateUtils';
import { formatPlatformName } from '../helperService';
import { buildTsv } from '../../utils/tsvUtils';

/**
 * One row per product per order — the line items behind a list of orders. Shared by the
 * Excel export's "Product Details" sheet and the Orders tab's "Copy line items" action, so
 * the two can never disagree on which rows exist, in which order, or with which numbers.
 *
 * These nine fields are exactly the sheet's columns. Nothing may be added here: `json_to_sheet`
 * ORDERS by its `header` argument but still APPENDS any key the array doesn't list, so an extra
 * field would silently grow the shipped Excel file by a column.
 */
export interface OrderLineItemSheetRow {
  productName: string;
  articleId: string;
  supplier: string;
  category: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  orderNumber: string;
  date: string;
}

/** The same row plus the cells only the clipboard renders — never handed to the Excel writer. */
interface OrderLineItemRow extends OrderLineItemSheetRow {
  /** Human-readable supplier ("Bidfood", not the `bidfood` enum slug). */
  supplierDisplay: string;
  /** Sortable in a spreadsheet, unlike the locale-dependent `date`. */
  isoDate: string;
  /** Only resolved in the All Restaurants view; '' otherwise. */
  restaurantName: string;
}

interface ProductOccurrence {
  orderNumber: string;
  date: string;
  isoDate: string;
  restaurantName: string;
  quantity: number;
  price: number;
}

interface ProductGroup {
  name: string;
  articleId: string;
  supplierName: string;
  supplierDisplay: string;
  category: string;
  orders: ProductOccurrence[];
}

/**
 * Groups every line across the given orders by product, keeping each occurrence. This is what
 * gives the export its row ORDER: grouped by product, not chronological.
 */
function groupLineItemsByProduct(
  orders: OrderHistory[],
  showExVat: boolean,
  restaurantNameOf?: (order: OrderHistory) => string
): ProductGroup[] {
  const productMap = new Map<string, ProductGroup>();

  orders.forEach(order => {
    // Use unknownSupplierName if platform is any_invoice, otherwise use platform
    const supplierName = order.platform === 'any_invoice' && order.unknownSupplierName
      ? order.unknownSupplierName
      : order.platform;
    const supplierDisplay = order.platform === 'any_invoice' && order.unknownSupplierName
      ? order.unknownSupplierName
      : formatPlatformName(order.platform);

    const orderDate = toDate(order.orderDate);
    const occurrenceBase = {
      orderNumber: order.orderNumber,
      date: getTableFormattedDate(orderDate),
      isoDate: format(orderDate, 'yyyy-MM-dd'),
      restaurantName: restaurantNameOf ? restaurantNameOf(order) : '',
    };

    order.productData.forEach(product => {
      // Create a unique key for each product (articleId + platform + name)
      // This handles cases where articleId might be empty for some platforms
      const key = `${product.articleId || 'no-id'}_${order.platform}_${product.name}`;

      const price = showExVat ? product.price : (product.priceWithTaxes || product.price);
      const occurrence: ProductOccurrence = { ...occurrenceBase, quantity: product.quantity, price };

      const existing = productMap.get(key);
      if (existing) {
        existing.orders.push(occurrence);
        return;
      }

      productMap.set(key, {
        name: product.name,
        articleId: product.articleId || '',
        supplierName,
        supplierDisplay,
        category: product.category || 'Uncategorized',
        orders: [occurrence],
      });
    });
  });

  return Array.from(productMap.values());
}

/**
 * @param restaurantNameOf - resolves an order's restaurant, for the All Restaurants view. Omitted
 *                           by the Excel export, which has no restaurant column.
 */
function buildRows(
  orders: OrderHistory[],
  showExVat: boolean,
  restaurantNameOf?: (order: OrderHistory) => string
): OrderLineItemRow[] {
  const rows: OrderLineItemRow[] = [];

  groupLineItemsByProduct(orders, showExVat, restaurantNameOf).forEach(product => {
    // For each product, create a row for each order it appears in
    product.orders.forEach(orderInfo => {
      rows.push({
        productName: product.name,
        articleId: product.articleId,
        supplier: product.supplierName,
        category: product.category,
        quantity: orderInfo.quantity,
        unitPrice: orderInfo.price,
        totalPrice: orderInfo.price * orderInfo.quantity,
        orderNumber: orderInfo.orderNumber,
        date: orderInfo.date,
        supplierDisplay: product.supplierDisplay,
        isoDate: orderInfo.isoDate,
        restaurantName: orderInfo.restaurantName,
      });
    });
  });

  return rows;
}

/**
 * The rows of the Excel export's "Product Details" sheet: the nine sheet columns and nothing else,
 * so the clipboard-only cells can never leak into the file.
 */
export function buildOrderLineItemRows(orders: OrderHistory[], showExVat: boolean): OrderLineItemSheetRow[] {
  return buildRows(orders, showExVat).map(
    ({ supplierDisplay, isoDate, restaurantName, ...sheetRow }) => sheetRow
  );
}

/** Column headers for the clipboard export, keyed so headers and cells can't drift apart. */
export interface OrderLineItemLabels {
  productName: string;
  articleId: string;
  supplier: string;
  restaurant: string;
  category: string;
  quantity: string;
  unitPrice: string;
  totalPrice: string;
  orderNumber: string;
  date: string;
}

/** Quantity is a whole count on most platforms and a fraction on the per-kg ones — neither wants `3.00`. */
const formatQuantity = (quantity: number): string => String(Number(quantity.toFixed(3)));

/**
 * The line items as tab-separated text: the same rows, in the same order, with the same numbers
 * as the Excel "Product Details" sheet. Only the cell RENDERING differs — translated headers, a
 * sortable ISO date, a human-readable supplier — because this one is pasted, not parsed.
 */
export function buildOrderLineItemsClipboardText(
  orders: OrderHistory[],
  showExVat: boolean,
  labels: OrderLineItemLabels,
  options: { includeRestaurant?: boolean; restaurantNameOf?: (order: OrderHistory) => string } = {}
): string {
  const { includeRestaurant = false, restaurantNameOf } = options;

  const headers = [
    labels.productName,
    labels.articleId,
    labels.supplier,
    ...(includeRestaurant ? [labels.restaurant] : []),
    labels.category,
    labels.quantity,
    labels.unitPrice,
    labels.totalPrice,
    labels.orderNumber,
    labels.date,
  ];

  const rows = buildRows(orders, showExVat, restaurantNameOf).map(row => [
    row.productName,
    row.articleId,
    row.supplierDisplay,
    ...(includeRestaurant ? [row.restaurantName] : []),
    row.category,
    formatQuantity(row.quantity),
    row.unitPrice.toFixed(2),
    row.totalPrice.toFixed(2),
    row.orderNumber,
    row.isoDate,
  ]);

  return buildTsv(headers, rows);
}
